import { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const SEO = {
  en: {
    htmlLang: 'en',
    title: 'XINVANA – Custom 3D Printed Shoes | Perfect Fit Guaranteed',
    description: 'Shoes made exactly for your feet. Scan your precise foot measurements and get a perfectly fitting pair with XINVANA 3D printing technology.',
    ogLocale: 'en_US',
    keywords: 'custom shoes, 3D printed shoes, foot measurement, perfect fit, personalized footwear',
  },
  ko: {
    htmlLang: 'ko',
    title: 'XINVANA – 맞춤형 3D 프린팅 신발 | 내 발에 꼭 맞는 신발',
    description: '내 발에 꼭 맞는 신발을 3D 프린팅으로 제작합니다. 발 사이즈를 정밀 측정하고 XINVANA만의 맞춤 제작 기술로 완벽한 핏을 경험하세요.',
    ogLocale: 'ko_KR',
    keywords: '맞춤 신발, 3D 프린팅 신발, 발 측정, 맞춤 제작, 퍼스널라이즈 신발',
  },
  zh: {
    htmlLang: 'zh-CN',
    title: 'XINVANA – 定制3D打印鞋 | 精准足型测量，完美合脚',
    description: '专为您的双脚量身定制。通过精准足部尺寸测量，利用XINVANA 3D打印技术制作完美合脚的鞋子。',
    ogLocale: 'zh_CN',
    keywords: '定制鞋, 3D打印鞋, 足部测量, 完美合脚, 个性化鞋履',
  },
  ja: {
    htmlLang: 'ja',
    title: 'XINVANA – カスタム3Dプリントシューズ | あなたの足に完璧なフィット',
    description: 'あなたの足型に合わせて作るオーダーメイドシューズ。精密な足型測定とXINVANAの3Dプリント技術で、完璧なフィット感を実現します。',
    ogLocale: 'ja_JP',
    keywords: 'カスタムシューズ, 3Dプリントシューズ, 足型測定, 完璧なフィット, オーダーメイド靴',
  },
  es: {
    htmlLang: 'es',
    title: 'XINVANA – Zapatos 3D a Medida | Ajuste Perfecto Garantizado',
    description: 'Zapatos fabricados exactamente para tus pies. Escanea tus medidas con precisión y obtén el calce perfecto con la tecnología de impresión 3D de XINVANA.',
    ogLocale: 'es_ES',
    keywords: 'zapatos a medida, zapatos 3D, medición de pies, calce perfecto, calzado personalizado',
  },
  it: {
    htmlLang: 'it',
    title: 'XINVANA – Scarpe 3D su Misura | Vestibilità Perfetta Garantita',
    description: 'Scarpe create su misura per i tuoi piedi. Misura con precisione le dimensioni del piede e ottieni la vestibilità perfetta con la stampa 3D XINVANA.',
    ogLocale: 'it_IT',
    keywords: 'scarpe su misura, scarpe stampate 3D, misurazione piede, vestibilità perfetta, calzature personalizzate',
  },
  de: {
    htmlLang: 'de',
    title: 'XINVANA – Maßgeschneiderte 3D-gedruckte Schuhe | Perfekte Passform garantiert',
    description: 'Schuhe, die exakt für Ihre Füße gefertigt werden. Messen Sie Ihre Füße präzise und erhalten Sie mit der 3D-Drucktechnologie von XINVANA eine perfekte Passform.',
    ogLocale: 'de_DE',
    keywords: 'maßgeschneiderte Schuhe, 3D-gedruckte Schuhe, Fußmessung, perfekte Passform, personalisiertes Schuhwerk',
  },
  nl: {
    htmlLang: 'nl',
    title: 'XINVANA – Op Maat Gemaakte 3D-geprinte Schoenen | Perfecte Pasvorm Gegarandeerd',
    description: 'Schoenen die precies voor uw voeten zijn gemaakt. Meet uw voetmaten nauwkeurig en krijg een perfecte pasvorm met de 3D-printtechnologie van XINVANA.',
    ogLocale: 'nl_NL',
    keywords: 'op maat gemaakte schoenen, 3D-geprinte schoenen, voetmeting, perfecte pasvorm, gepersonaliseerd schoeisel',
  },
};

function setMeta(selector, value, attrName = 'content') {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    // Parse selector to set the identifying attribute
    const match = selector.match(/\[(\w+(?::\w+)?)="([^"]+)"\]/);
    if (match) el.setAttribute(match[1], match[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attrName, value);
}

function setLink(rel, hreflang, href) {
  const selector = `link[rel="${rel}"][hreflang="${hreflang}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

const CANONICAL = 'https://xinvana.com';

export default function SeoHead() {
  const { geoLang } = useLanguage();

  useEffect(() => {
    const seo = SEO[geoLang] || SEO.en;

    document.documentElement.lang = seo.htmlLang;
    document.title = seo.title;

    // Standard meta
    setMeta('meta[name="description"]', seo.description);
    setMeta('meta[name="keywords"]', seo.keywords);
    setMeta('meta[name="robots"]', 'index, follow');

    // Open Graph
    setMeta('meta[property="og:title"]', seo.title);
    setMeta('meta[property="og:description"]', seo.description);
    setMeta('meta[property="og:locale"]', seo.ogLocale);
    setMeta('meta[property="og:type"]', 'website');
    setMeta('meta[property="og:url"]', CANONICAL);
    setMeta('meta[property="og:site_name"]', 'XINVANA');

    // Twitter Card
    setMeta('meta[name="twitter:card"]', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', seo.title);
    setMeta('meta[name="twitter:description"]', seo.description);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);

    // hreflang — same URL serves all languages (SPA)
    const hreflangs = [
      ['en', 'en'], ['ko', 'ko'], ['zh-CN', 'zh'], ['ja', 'ja'],
      ['es', 'es'], ['it', 'it'], ['de', 'de'], ['nl', 'nl'], ['x-default', 'en'],
    ];
    hreflangs.forEach(([tag]) => setLink('alternate', tag, CANONICAL));
  }, [geoLang]);

  return null;
}
